import { cn } from "@/lib/utils";

export interface CardData {
  type: "number" | "action" | "modifier";
  value: number | null;
  subtype: string | null;
}

interface CardProps {
  card?: CardData;
  faceDown?: boolean;
  compact?: boolean;
  mini?: boolean;
  className?: string;
}

const ACTION_LABELS: Record<string, string> = {
  freeze: "FREEZE",
  flip_three: "FLIP 3",
  second_chance: "2ND CHANCE",
};

const ACTION_COLORS: Record<string, string> = {
  freeze: "bg-blue-200 border-blue-400 text-blue-900",
  flip_three: "bg-purple-200 border-purple-400 text-purple-900",
  second_chance: "bg-green-200 border-green-400 text-green-900",
};

function ModifierLabel({ card }: { card: CardData }) {
  if (card.subtype === "times") return <span className="text-lg font-bold">×2</span>;
  return <span className="text-lg font-bold">+{card.value}</span>;
}

export default function Card({ card, faceDown = false, compact = false, mini = false, className }: CardProps) {
  const size = mini
    ? "w-6 h-8"
    : compact
      ? "w-8 h-12 md:w-10 md:h-14"
      : "w-10 h-14 md:w-14 md:h-20";

  if (faceDown || !card) {
    return (
      <div
        className={cn(
          size,
          "rounded-sm border-2 border-[var(--win2k-dark-shadow)] shrink-0",
          "bg-[repeating-conic-gradient(#1a365d_0%_25%,_#2b6cb0_0%_50%)] bg-[length:8px_8px]",
          className
        )}
      />
    );
  }

  if (card.type === "number") {
    if (mini) {
      return (
        <div
          className={cn(
            size,
            "rounded-sm border border-gray-400 bg-white shrink-0",
            "flex items-center justify-center",
            className
          )}
        >
          <span className="text-[10px] font-bold text-red-700">{card.value}</span>
        </div>
      );
    }
    return (
      <div
        className={cn(
          size,
          "rounded-sm border border-gray-400 bg-white shrink-0",
          "flex flex-col items-center justify-between p-0.5 relative",
          className
        )}
      >
        <span className="text-[8px] md:text-[10px] font-bold text-red-700 self-start leading-none">
          {card.value}
        </span>
        <span className="text-sm md:text-xl font-bold text-red-700">
          {card.value}
        </span>
        <span className="text-[8px] md:text-[10px] font-bold text-red-700 self-end leading-none rotate-180">
          {card.value}
        </span>
      </div>
    );
  }

  if (card.type === "action") {
    const sub = card.subtype ?? "freeze";
    return (
      <div
        className={cn(
          size,
          "rounded-sm border shrink-0",
          "flex items-center justify-center p-0.5 text-center",
          ACTION_COLORS[sub] ?? "bg-gray-200 border-gray-400",
          className
        )}
      >
        <span className={cn("font-bold leading-tight", mini ? "text-[5px]" : "text-[6px] md:text-[8px]")}>
          {ACTION_LABELS[sub] ?? sub}
        </span>
      </div>
    );
  }

  if (card.type === "modifier") {
    return (
      <div
        className={cn(
          size,
          "rounded-sm border border-amber-400 bg-amber-100 shrink-0",
          "flex items-center justify-center",
          className
        )}
      >
        <span className={cn("font-bold text-amber-800", mini ? "text-[9px]" : "text-xs md:text-sm")}>
          {mini ? (card.subtype === "times" ? "×2" : `+${card.value}`) : <ModifierLabel card={card} />}
        </span>
      </div>
    );
  }

  return null;
}
